import { Component, OnInit, Input } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { ReferenceCodeService } from '@app/admin/reference-code/reference-code.service';
import { ApplicationService } from '@app/applications/application.service';
import { LoanApplication, ApplicationAccessSpecifier } from '@app/applications/applications.model';
import { ReferenceCode } from '@app/admin/reference-code/reference-code.model';
import { ValidationService } from '../../../application-validators/validation.service'
import { MatSnackBar } from '@angular/material';
import { ConfirmationDialogService } from '@app/utils/confirmation-dialog/confirmation-dialog.service';
import { FileUploadService } from '@app/utils/file-upload/file-upload.service';
import { FolderFilesComposite } from '@app/applications/application-details/loanInformation/loan-folder-files/loan-folder-files.model';
import * as fileSaver from 'file-saver';
import { ImagePreviewDialogService } from '@app/utils/image-preview-dialog/image-preview-dialog.service';
import { IgFileService } from '@ig-core/form/igFile.service';
import { Applicant } from '../../collateral/collateralDetails/collateral-details.model';
import { SETTING_DATAFORMAT_CKYC_NUMBER_FORMAT } from '@app/constants/data.constants';
import { ControlValidators } from '@app/applications/application-validators/control-validators.directive';
@Component({
  selector: 'eng-ckyc-proof',
  templateUrl: 'ckyc.template.html',
  styleUrls: ['../../application-details.styles.scss', './ckyc.scss']
})
export class CkycComponent implements OnInit {

  @Input()
  application: LoanApplication;
  @Input()
  menuCode: string;

  borrowers: Applicant[];
  selectedBorrower: Applicant;
  ckycForm: FormGroup;
  showForm: boolean;

  isFormDisabled: boolean;
  allowAccess: boolean;
  menuItemAllowAccess: boolean;

  allFilesInFolder: FolderFilesComposite;
  ckycNumberFormat: ReferenceCode[];
  settingsList: ReferenceCode[] = [];
  ckycGuideUrl: string;

  applicationAccessSpecifiers: ApplicationAccessSpecifier[];
  menuItemAccessSpecifier: ApplicationAccessSpecifier;
  constructor(private formBuilder: FormBuilder,
    private referenceCodeService: ReferenceCodeService,
    private applicationService: ApplicationService,
    private _snackbar: MatSnackBar,
    private fileUploadService: FileUploadService,
    private imagePreviewDialogService: ImagePreviewDialogService,
    private confirmationDialogService: ConfirmationDialogService,
    private validationService: ValidationService,
    private fileService: IgFileService) {
    this.ckycForm = this.formBuilder.group({
      ckycAvailability: ['', ControlValidators.requiredValidator("Select CKYC availability")],
      ckycNumber: ''
    });
    this.allowAccess = this.applicationService.allowAccess;
  }

  ngOnInit() {
    this.isFormDisabled = true;
    this.getRefCodes();
    this.fetchApplicants();
    this.getMenuItemAccess();
  }

  getRefCodes() {
    this.referenceCodeService.getRefCodesForClassifier('setting_dataformat').subscribe((response: any) => {
      this.ckycNumberFormat = response.filter(ele => ele.code.toUpperCase() === SETTING_DATAFORMAT_CKYC_NUMBER_FORMAT);
    });

    this.referenceCodeService.getRefCodesForClassifier('settings').subscribe((response: any) => {
      this.settingsList = response;
      this.settingsList.forEach(element => {
        if (element.code == "CKYC-SEARCH-GUIDE-URL") {
          this.ckycGuideUrl = element.field1 ? element.field1.trim() : null;
        }
      });
    });
  }

  fetchApplicants() {
    this.showForm = false;
    this.applicationService.getApplicants(this.application.uid).subscribe((response: any) => {
      if (response) {
        this.borrowers = response;
        if (this.borrowers && this.borrowers.length > 0) {
          this.showForm = true;
          this.buildCkycForm(this.borrowers[0]);
        }
      }
    })
  }

  changeSelectedBorrower(borrower?: Applicant) {
    this.showForm = true;
    this.buildCkycForm(borrower);
  }

  buildCkycForm(borrowerDetails?: any) {
    if (this.showForm) {
      this.ckycForm.patchValue({
        ckycAvailability: borrowerDetails.ckycNumber === 'NA' ? 'NA' : (borrowerDetails.ckycNumber ? 'AVAILABLE' : ''),
        ckycNumber: borrowerDetails.ckycNumber
      })

      this.checkCkycAvailability(this.ckycForm.controls['ckycAvailability'].value);
      this.selectedBorrower = borrowerDetails;

      this.getSelectedFolderFiles(this.selectedBorrower.ckycFolderUid);
    }
    this.ckycForm.disable();
  }

  checkCkycAvailability(value: string) {
    const ckycNumberControl = this.ckycForm.controls['ckycNumber'];

    if (value === 'NA') {
      ckycNumberControl.setValue('');
      ckycNumberControl.clearValidators();
      ckycNumberControl.disable();
    } else if (value === 'AVAILABLE') {
      ckycNumberControl.enable();
      // Default pattern for CKYC number
      ckycNumberControl.setValidators([
        ControlValidators.requiredValidator("CKYC number is required"),
        ControlValidators.patternValidator('^[0-9]{14}$', "CKYC number should be 14 digits")
      ]);

      // If ckycNumberFormat regex is present in field1 and regex is valid,
      // use field1 regex
      if (
        this.ckycNumberFormat && this.ckycNumberFormat.length > 0 &&
        this.ckycNumberFormat[0].field1.trim() !== '' &&
        this.validationService.isValidRegexPattern(this.ckycNumberFormat[0].field1)
      ) {
        ckycNumberControl.setValidators([
          ControlValidators.requiredValidator("CKYC number is required"),
          ControlValidators.patternValidator(this.ckycNumberFormat[0].field1.trim(), "CKYC number does not match required format")
        ]);
      }
    }

    ckycNumberControl.updateValueAndValidity();
  }

  saveBorrower() {
    this.validationService.markFormGroupTouched(this.ckycForm)
    if (!this.isFormDisabled && this.ckycForm.valid) {
      let availability = this.ckycForm.controls['ckycAvailability'].value;
      this.selectedBorrower.ckycNumber = (availability === 'NA' ? 'NA' : this.ckycForm.controls['ckycNumber'].value);
      this.applicationService.saveborrowerCkyc(this.application.uid, this.selectedBorrower.uid, this.selectedBorrower).toPromise().then(
        (_success) => {
          this._snackbar.open("Borrower details updated successfully", "Close", {
            duration: 2000,
          });
          this.isFormDisabled = true
          this.fetchApplicants();
        }, (failure) => {
          let errormesg = []
          errormesg.push(failure)
          this.applicationService.displayErrorMessages(errormesg);
        }
      );
    }
  }

  enableDisableForm() {
    this.ckycForm.enable()
    this.checkCkycAvailability(this.ckycForm.controls['ckycAvailability'].value);
    this.isFormDisabled = false;
  }

  cancelForm() {
    if (this.borrowers && this.borrowers.length > 0) {
      this.buildCkycForm(this.borrowers[0]);
    } else {
      this.showForm = false;
    }
    this.isFormDisabled = true;
    this.ckycForm.disable();
  }

  //this function will get either this menu item  is editable or readonly 
  getMenuItemAccess() {
    this.applicationService.getApplicationAccessModifiers(this.application.uid)
      .subscribe((response) => {
        this.applicationAccessSpecifiers = response.body;
        this.menuItemAccessSpecifier = this.applicationAccessSpecifiers
          .find(accessSpecifier => accessSpecifier.category === this.menuCode);
        if (this.menuItemAccessSpecifier) {
          this.menuItemAllowAccess = this.menuItemAccessSpecifier.allowAccess;
        }
      });
  }

  //this function open up the image upload dailog box
  //if file upload should allow single and multiple file upload, we need to pass "uploadType":"multiple"
  openImageFileUploadDialog() {
    if (this.selectedBorrower.ckycFolderUid != null) {
      this.fileUploadService.open({ "uploadType": "multiple", "folderUid": this.selectedBorrower.ckycFolderUid, "applicationUid": this.application.uid, "files": this.allFilesInFolder.fileInfo })
      this.fileUploadService.confirmed().subscribe(data => {
        this.getSelectedFolderFiles(data.folderUid)
      })

    } else {
      this._snackbar.open("Please add a folder to upload files", "Close", {
        duration: 5000,
      });
    }
  }

  getSelectedFolderFiles(ckycFolderUid) {
    if (ckycFolderUid) {
      this.applicationService.getAllFilesInApplicationFolder(this.application.uid, ckycFolderUid).subscribe((response: any) => {
        if (response) {
          this.allFilesInFolder = response.body;
          this.allFilesInFolder.fileInfo.forEach(element => {
            if (element.fileSize) {
              element["convertedFileSize"] = this.applicationService.bytesToSize(element.fileSize)
            }
          });
        }
      });
    }
  }

  //this function will call onclick of download icon in file's section
  downloadFile(file) {
    this.applicationService.downloadFileFromApplicationFolder(this.application.uid, file.ckycFolderUid, file.fileId).subscribe(image => {
      const blob = new Blob([image.data], { type: file.type });
      fileSaver.saveAs(blob, file.fileName);
      this._snackbar.open("File has been downloaded successfully", "Close", {
        duration: 5000,
      });
    })
  }

  //this function will trigger onclicking of image,will open up a mat-dailog
  openContentElement() {
    this.imagePreviewDialogService.open({ "images": this.allFilesInFolder })
  }


  openfileDeleteConfirmation(selectedFile) {
    this.confirmationDialogService.open({ "btnCancelText": "Cancel", "btnConfirmText": "Delete", "message": "Are you sure you want to delete this File?" })
    this.confirmationDialogService.confirmed().subscribe(data => {
      if (data) {
        this.deleteFile(selectedFile)
      }
    })

  }

  //this function will be called on click of delete icon on file list
  deleteFile(selectedFile) {
    this.applicationService.fileDelete(this.application.uid, selectedFile.uid).subscribe((response) => {
      if (response.body.status == "success") {
        this._snackbar.open("File deleted successfully", "Close", {
          duration: 2000,
        });
        this.getSelectedFolderFiles(this.selectedBorrower.ckycFolderUid);
      } else {
        let errormesg = []
        errormesg.push("File deleted failed")
        errormesg.push(response.body.message)
        this.applicationService.displayErrorMessages(errormesg);
      }
    }, (failure) => {
      let errormesg = []
      errormesg.push("File deleted failed")
      errormesg.push(failure)
      this.applicationService.displayErrorMessages(errormesg);
    });
  }

  viewPdf(file) {
    this.fileService.fileStreamById(file.fileId, "false").subscribe(res => {
      window.open(res.data, '_blank');
    })
  }

}